import { Quat, Vec3, math } from 'playcanvas';
import type { Entity } from 'playcanvas';

import type { Collision, PushOut, RayHit } from '../collision';
import { DEFAULT_VOXEL_RESOLUTION, PENETRATION_EPSILON } from '../collision/collision';
import type { SceneObject, SceneObjectColliderConfig } from './SceneObjectTypes';

const VIDEO_COLLIDER_MIN_THICKNESS = 0.2;
const GLB_COLLIDER_MIN_SIZE = 0.2;
const CONFIGURED_COLLIDER_MIN_SIZE = 0.05;
const SURFACE_NORMAL_EPSILON = 0.08;
const CAPSULE_SAMPLE_COUNT = 5;

interface ObjectBox {
    kind: 'box';
    object: SceneObject;
    center: Vec3;
    axisX: Vec3;
    axisY: Vec3;
    axisZ: Vec3;
    halfX: number;
    halfY: number;
    halfZ: number;
}

interface ObjectSphere {
    kind: 'sphere';
    object: SceneObject;
    center: Vec3;
    radius: number;
}

interface ObjectCylinder {
    kind: 'cylinder';
    object: SceneObject;
    center: Vec3;
    axisX: Vec3;
    axisY: Vec3;
    axisZ: Vec3;
    radius: number;
    halfHeight: number;
}

interface ObjectCapsule {
    kind: 'capsule';
    object: SceneObject;
    center: Vec3;
    axisX: Vec3;
    axisY: Vec3;
    axisZ: Vec3;
    radius: number;
    halfSegment: number;
    halfHeight: number;
}

type ObjectCollider = ObjectBox | ObjectSphere | ObjectCylinder | ObjectCapsule;

type MarkerEntityGetter = (id: string) => Entity | undefined;
type ObjectRuntimeVisibility = (object: SceneObject) => boolean;

interface LocalPoint {
    x: number;
    y: number;
    z: number;
}

interface MeshInstanceBoundsLike {
    aabb?: {
        center: Vec3;
        halfExtents: Vec3;
    };
}

interface MeshComponentBoundsLike {
    meshInstances?: MeshInstanceBoundsLike[];
    model?: {
        meshInstances?: MeshInstanceBoundsLike[];
    };
}

const _quat = new Quat();
const _axisX = new Vec3();
const _axisY = new Vec3();
const _axisZ = new Vec3();
const _delta = new Vec3();
const _scratchPush: PushOut = { x: 0, y: 0, z: 0 };
const _worldAxisX = new Vec3(1, 0, 0);
const _worldAxisY = new Vec3(0, 1, 0);
const _worldAxisZ = new Vec3(0, 0, 1);

class SceneObjectCollision implements Collision {
    readonly baseCollision: Collision | null;

    private readonly _objects: () => readonly SceneObject[];
    private readonly _getMarkerEntity: MarkerEntityGetter;
    private readonly _runtimeVisibility: ObjectRuntimeVisibility;
    private readonly _basePush: PushOut = { x: 0, y: 0, z: 0 };
    private readonly _objectPush: PushOut = { x: 0, y: 0, z: 0 };
    private readonly _normalResult = { nx: 0, ny: 1, nz: 0 };

    constructor(
        baseCollision: Collision | null,
        objects: () => readonly SceneObject[],
        getMarkerEntity: MarkerEntityGetter = () => undefined,
        runtimeVisibility: ObjectRuntimeVisibility = defaultObjectRuntimeVisibility
    ) {
        this.baseCollision = baseCollision;
        this._objects = objects;
        this._getMarkerEntity = getMarkerEntity;
        this._runtimeVisibility = runtimeVisibility;
    }

    get voxelResolution(): number {
        return this.baseCollision?.voxelResolution ?? DEFAULT_VOXEL_RESOLUTION;
    }

    queryRay(
        ox: number, oy: number, oz: number,
        dx: number, dy: number, dz: number,
        maxDist: number
    ): RayHit | null {
        const baseHit = this.baseCollision?.queryRay(ox, oy, oz, dx, dy, dz, maxDist) ?? null;
        const objectHit = this._queryObjectRay(ox, oy, oz, dx, dy, dz, maxDist);
        if (!baseHit) return objectHit;
        if (!objectHit) return baseHit;
        return distanceSq(ox, oy, oz, objectHit) < distanceSq(ox, oy, oz, baseHit)
            ? objectHit
            : baseHit;
    }

    querySphere(cx: number, cy: number, cz: number, radius: number, out: PushOut): boolean {
        let found = false;
        let bestSq = 0;

        if (this.baseCollision?.querySphere(cx, cy, cz, radius, this._basePush)) {
            bestSq = pushSq(this._basePush);
            copyPush(this._basePush, out);
            found = true;
        }

        for (const collider of this._colliders()) {
            if (!sphereObjectPenetration(collider, cx, cy, cz, radius, this._objectPush)) continue;
            const sq = pushSq(this._objectPush);
            if (!found || sq > bestSq) {
                bestSq = sq;
                copyPush(this._objectPush, out);
                found = true;
            }
        }

        return found;
    }

    queryCapsule(cx: number, cy: number, cz: number, halfHeight: number, radius: number, out: PushOut): boolean {
        let found = false;
        let bestSq = 0;

        if (this.baseCollision?.queryCapsule(cx, cy, cz, halfHeight, radius, this._basePush)) {
            bestSq = pushSq(this._basePush);
            copyPush(this._basePush, out);
            found = true;
        }

        for (const collider of this._colliders()) {
            if (!capsuleObjectPenetration(collider, cx, cy, cz, halfHeight, radius, this._objectPush)) continue;
            const sq = pushSq(this._objectPush);
            if (!found || sq > bestSq) {
                bestSq = sq;
                copyPush(this._objectPush, out);
                found = true;
            }
        }

        return found;
    }

    querySurfaceNormal(
        x: number, y: number, z: number,
        rdx: number, rdy: number, rdz: number
    ): { nx: number; ny: number; nz: number } {
        for (const collider of this._colliders()) {
            const normal = objectSurfaceNormal(collider, x, y, z, rdx, rdy, rdz);
            if (!normal) continue;
            this._normalResult.nx = normal.x;
            this._normalResult.ny = normal.y;
            this._normalResult.nz = normal.z;
            return this._normalResult;
        }
        return this.baseCollision?.querySurfaceNormal(x, y, z, rdx, rdy, rdz) ?? this._normalResult;
    }

    isFreeAt(x: number, y: number, z: number): boolean {
        if (this.baseCollision && !this.baseCollision.isFreeAt(x, y, z)) return false;
        for (const collider of this._colliders()) {
            if (isInsideObjectCollider(collider, x, y, z)) return false;
        }
        return true;
    }

    private *_colliders(): Iterable<ObjectCollider> {
        for (const object of this._objects()) {
            const collider = makeObjectCollider(object, this._getMarkerEntity, this._runtimeVisibility);
            if (collider) yield collider;
        }
    }

    private _queryObjectRay(
        ox: number, oy: number, oz: number,
        dx: number, dy: number, dz: number,
        maxDist: number
    ): RayHit | null {
        let bestHit: RayHit | null = null;
        let bestT = Infinity;
        for (const collider of this._colliders()) {
            const t = rayObjectCollider(collider, ox, oy, oz, dx, dy, dz, maxDist);
            if (t === null || t >= bestT) continue;
            bestT = t;
            bestHit = {
                x: ox + normalizeComponent(dx, dy, dz, dx) * t,
                y: oy + normalizeComponent(dx, dy, dz, dy) * t,
                z: oz + normalizeComponent(dx, dy, dz, dz) * t
            };
        }
        return bestHit;
    }
}

function createSceneObjectCollision(
    baseCollision: Collision | null,
    objects: () => readonly SceneObject[],
    getMarkerEntity?: MarkerEntityGetter,
    runtimeVisibility: ObjectRuntimeVisibility = defaultObjectRuntimeVisibility
): Collision | null {
    if (!baseCollision && !objects().some((object) => hasObjectCollider(object, () => true))) return null;
    return new SceneObjectCollision(baseCollision, objects, getMarkerEntity, runtimeVisibility);
}

function defaultObjectRuntimeVisibility(object: SceneObject): boolean {
    return object.type !== 'videoScreen' || object.video?.defaultVisible !== false;
}

function hasObjectCollider(
    object: SceneObject,
    runtimeVisibility: ObjectRuntimeVisibility = defaultObjectRuntimeVisibility
): boolean {
    if (object.type === 'videoScreen' && !runtimeVisibility(object)) return false;
    if (object.collider) return true;
    const marker = object.marker;
    return marker.kind === 'glb' || marker.kind === 'video';
}

function makeObjectCollider(
    object: SceneObject,
    getMarkerEntity: MarkerEntityGetter,
    runtimeVisibility: ObjectRuntimeVisibility = defaultObjectRuntimeVisibility
): ObjectCollider | null {
    if (!hasObjectCollider(object, runtimeVisibility)) return null;
    if (object.collider) return makeConfiguredCollider(object, object.collider);
    return makeObjectBox(object, getMarkerEntity);
}

function makeObjectBox(object: SceneObject, getMarkerEntity: MarkerEntityGetter): ObjectBox | null {
    if (object.collider) return null;

    const marker = object.marker;
    if (marker.kind === 'glb') {
        const glbBounds = makeGlbBoundsBox(object, getMarkerEntity(object.id));
        if (glbBounds) return glbBounds;
    }

    const center = new Vec3(
        object.transform.position[0],
        object.transform.position[1],
        object.transform.position[2]
    );
    _quat.setFromEulerAngles(
        object.transform.rotation[0],
        object.transform.rotation[1],
        object.transform.rotation[2]
    );
    _quat.transformVector(Vec3.RIGHT, _axisX);
    _quat.transformVector(Vec3.UP, _axisY);
    _quat.transformVector(Vec3.FORWARD, _axisZ);

    if (marker.kind === 'video') {
        const scale = marker.scale ?? object.transform.scale;
        return {
            kind: 'box',
            object,
            center,
            axisX: _axisX.clone(),
            axisY: _axisY.clone(),
            axisZ: _axisZ.clone(),
            halfX: Math.max(Math.abs(scale[0]), VIDEO_COLLIDER_MIN_THICKNESS) * 0.5,
            halfY: Math.max(Math.abs(scale[1]), VIDEO_COLLIDER_MIN_THICKNESS) * 0.5,
            halfZ: Math.max(Math.abs(scale[2]), VIDEO_COLLIDER_MIN_THICKNESS) * 0.5
        };
    } else if (marker.kind === 'glb') {
        const scale = fallbackGlbScale(marker.scale ?? object.transform.scale[0]);
        return {
            kind: 'box',
            object,
            center,
            axisX: _axisX.clone(),
            axisY: _axisY.clone(),
            axisZ: _axisZ.clone(),
            halfX: Math.max(Math.abs(scale[0]), GLB_COLLIDER_MIN_SIZE) * 0.5,
            halfY: Math.max(Math.abs(scale[1]), GLB_COLLIDER_MIN_SIZE) * 0.5,
            halfZ: Math.max(Math.abs(scale[2]), GLB_COLLIDER_MIN_SIZE) * 0.5
        };
    } else {
        return null;
    }
}

function makeGlbBoundsBox(object: SceneObject, markerEntity: Entity | undefined): ObjectBox | null {
    if (!markerEntity) return null;
    const bounds = collectWorldAabb(markerEntity);
    if (!bounds) return null;
    return {
        kind: 'box',
        object,
        center: new Vec3(
            (bounds.minX + bounds.maxX) * 0.5,
            (bounds.minY + bounds.maxY) * 0.5,
            (bounds.minZ + bounds.maxZ) * 0.5
        ),
        axisX: _worldAxisX.clone(),
        axisY: _worldAxisY.clone(),
        axisZ: _worldAxisZ.clone(),
        halfX: Math.max(bounds.maxX - bounds.minX, GLB_COLLIDER_MIN_SIZE) * 0.5,
        halfY: Math.max(bounds.maxY - bounds.minY, GLB_COLLIDER_MIN_SIZE) * 0.5,
        halfZ: Math.max(bounds.maxZ - bounds.minZ, GLB_COLLIDER_MIN_SIZE) * 0.5
    };
}

function makeConfiguredCollider(object: SceneObject, config: SceneObjectColliderConfig): ObjectCollider | null {
    const center = new Vec3(
        object.transform.position[0],
        object.transform.position[1],
        object.transform.position[2]
    );
    _quat.setFromEulerAngles(
        object.transform.rotation[0],
        object.transform.rotation[1],
        object.transform.rotation[2]
    );
    _quat.transformVector(Vec3.RIGHT, _axisX);
    _quat.transformVector(Vec3.UP, _axisY);
    _quat.transformVector(Vec3.FORWARD, _axisZ);

    // For pure `collider` objects the collision volume always follows the
    // object transform: center sits at the object origin, size/radius/height
    // are derived from transform.scale, and rotation already drives the axes.
    const followTransform = object.type === 'collider';
    const offset = followTransform ? ([0, 0, 0] as [number, number, number]) : (config.center ?? [0, 0, 0]);
    center.x += _axisX.x * offset[0] + _axisY.x * offset[1] + _axisZ.x * offset[2];
    center.y += _axisX.y * offset[0] + _axisY.y * offset[1] + _axisZ.y * offset[2];
    center.z += _axisX.z * offset[0] + _axisY.z * offset[1] + _axisZ.z * offset[2];

    const visualScale = markerScaleVec(object);
    if (config.shape === 'box') {
        const size = followTransform ? visualScale : (config.size ?? visualScale);
        return {
            kind: 'box',
            object,
            center,
            axisX: _axisX.clone(),
            axisY: _axisY.clone(),
            axisZ: _axisZ.clone(),
            halfX: Math.max(Math.abs(size[0]), CONFIGURED_COLLIDER_MIN_SIZE) * 0.5,
            halfY: Math.max(Math.abs(size[1]), CONFIGURED_COLLIDER_MIN_SIZE) * 0.5,
            halfZ: Math.max(Math.abs(size[2]), CONFIGURED_COLLIDER_MIN_SIZE) * 0.5
        };
    }
    if (config.shape === 'sphere') {
        const radius = followTransform
            ? maxVecComponent(visualScale) * 0.5
            : (config.radius ?? maxVecComponent(visualScale) * 0.5);
        return {
            kind: 'sphere',
            object,
            center,
            radius: Math.max(Math.abs(radius), CONFIGURED_COLLIDER_MIN_SIZE)
        };
    }
    if (config.shape === 'cylinder') {
        const radius = followTransform
            ? Math.max(visualScale[0], visualScale[2]) * 0.5
            : (config.radius ?? Math.max(visualScale[0], visualScale[2]) * 0.5);
        const height = followTransform ? visualScale[1] : (config.height ?? visualScale[1]);
        return {
            kind: 'cylinder',
            object,
            center,
            axisX: _axisX.clone(),
            axisY: _axisY.clone(),
            axisZ: _axisZ.clone(),
            radius,
            halfHeight: height * 0.5
        };
    }
    if (config.shape === 'capsule') {
        const radius = followTransform
            ? Math.max(visualScale[0], visualScale[2]) * 0.5
            : (config.radius ?? Math.max(visualScale[0], visualScale[2]) * 0.5);
        const height = followTransform ? visualScale[1] : (config.height ?? visualScale[1]);
        return {
            kind: 'capsule',
            object,
            center,
            axisX: _axisX.clone(),
            axisY: _axisY.clone(),
            axisZ: _axisZ.clone(),
            radius,
            halfSegment: Math.max(0, height * 0.5 - radius),
            halfHeight: height * 0.5
        };
    }
    return null;
}

function collectWorldAabb(entity: Entity): { minX: number; minY: number; minZ: number; maxX: number; maxY: number; maxZ: number } | null {
    let minX = Infinity;
    let minY = Infinity;
    let minZ = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    let maxZ = -Infinity;
    let found = false;

    entity.forEach((node) => {
        const e = node as Entity;
        const render = e.render as unknown as MeshComponentBoundsLike | undefined;
        const model = e.model as unknown as MeshComponentBoundsLike | undefined;
        const meshInstances = [
            ...(render?.meshInstances ?? []),
            ...(model?.meshInstances ?? model?.model?.meshInstances ?? [])
        ];

        for (const mi of meshInstances) {
            const aabb = mi.aabb;
            if (!aabb) continue;
            minX = Math.min(minX, aabb.center.x - aabb.halfExtents.x);
            minY = Math.min(minY, aabb.center.y - aabb.halfExtents.y);
            minZ = Math.min(minZ, aabb.center.z - aabb.halfExtents.z);
            maxX = Math.max(maxX, aabb.center.x + aabb.halfExtents.x);
            maxY = Math.max(maxY, aabb.center.y + aabb.halfExtents.y);
            maxZ = Math.max(maxZ, aabb.center.z + aabb.halfExtents.z);
            found = true;
        }
    });

    return found ? { minX, minY, minZ, maxX, maxY, maxZ } : null;
}

function fallbackGlbScale(scale: number): [number, number, number] {
    const s = Math.max(Math.abs(scale), GLB_COLLIDER_MIN_SIZE);
    return [s, s, s];
}

function markerScaleVec(object: SceneObject): [number, number, number] {
    const marker = object.marker;
    switch (marker.kind) {
        case 'primitive':
        case 'video':
        case 'imagePanel':
            return marker.scale ?? object.transform.scale;
        case 'glb':
        case 'gsplat':
        case 'sprite': {
            const s = marker.scale ?? object.transform.scale[0] ?? 1;
            return [s, s, s];
        }
    }
}

function maxVecComponent(v: [number, number, number]): number {
    return Math.max(Math.abs(v[0]), Math.abs(v[1]), Math.abs(v[2]));
}

function toLocalPoint(box: ObjectBox, x: number, y: number, z: number): LocalPoint {
    _delta.set(x - box.center.x, y - box.center.y, z - box.center.z);
    return {
        x: _delta.dot(box.axisX),
        y: _delta.dot(box.axisY),
        z: _delta.dot(box.axisZ)
    };
}

function toLocalPointAxes(collider: ObjectCylinder | ObjectCapsule, x: number, y: number, z: number): LocalPoint {
    _delta.set(x - collider.center.x, y - collider.center.y, z - collider.center.z);
    return {
        x: _delta.dot(collider.axisX),
        y: _delta.dot(collider.axisY),
        z: _delta.dot(collider.axisZ)
    };
}

function localVectorToWorld(box: ObjectBox, x: number, y: number, z: number, out: PushOut): void {
    out.x = box.axisX.x * x + box.axisY.x * y + box.axisZ.x * z;
    out.y = box.axisX.y * x + box.axisY.y * y + box.axisZ.y * z;
    out.z = box.axisX.z * x + box.axisY.z * y + box.axisZ.z * z;
}

function localVectorToWorldAxes(
    collider: ObjectCylinder | ObjectCapsule,
    x: number,
    y: number,
    z: number,
    out: PushOut
): void {
    out.x = collider.axisX.x * x + collider.axisY.x * y + collider.axisZ.x * z;
    out.y = collider.axisX.y * x + collider.axisY.y * y + collider.axisZ.y * z;
    out.z = collider.axisX.z * x + collider.axisY.z * y + collider.axisZ.z * z;
}

function sphereObjectPenetration(collider: ObjectCollider, cx: number, cy: number, cz: number, radius: number, out: PushOut): boolean {
    switch (collider.kind) {
        case 'box':
            return sphereBoxPenetration(collider, cx, cy, cz, radius, out);
        case 'sphere':
            return sphereSpherePenetration(collider, cx, cy, cz, radius, out);
        case 'cylinder':
            return sphereCylinderPenetration(collider, cx, cy, cz, radius, out);
        case 'capsule':
            return sphereCapsulePenetration(collider, cx, cy, cz, radius, out);
    }
}

function sphereBoxPenetration(box: ObjectBox, cx: number, cy: number, cz: number, radius: number, out: PushOut): boolean {
    const p = toLocalPoint(box, cx, cy, cz);
    const qx = math.clamp(p.x, -box.halfX, box.halfX);
    const qy = math.clamp(p.y, -box.halfY, box.halfY);
    const qz = math.clamp(p.z, -box.halfZ, box.halfZ);
    const dx = p.x - qx;
    const dy = p.y - qy;
    const dz = p.z - qz;
    const distSq = dx * dx + dy * dy + dz * dz;
    if (distSq >= radius * radius) return false;

    if (distSq > 1e-12) {
        const dist = Math.sqrt(distSq);
        const push = radius - dist;
        localVectorToWorld(box, dx / dist * push, dy / dist * push, dz / dist * push, out);
        return pushSq(out) > PENETRATION_EPSILON * PENETRATION_EPSILON;
    }

    const faceX = box.halfX - Math.abs(p.x);
    const faceY = box.halfY - Math.abs(p.y);
    const faceZ = box.halfZ - Math.abs(p.z);
    if (faceX <= faceY && faceX <= faceZ) {
        localVectorToWorld(box, (p.x >= 0 ? 1 : -1) * (radius + faceX), 0, 0, out);
    } else if (faceY <= faceZ) {
        localVectorToWorld(box, 0, (p.y >= 0 ? 1 : -1) * (radius + faceY), 0, out);
    } else {
        localVectorToWorld(box, 0, 0, (p.z >= 0 ? 1 : -1) * (radius + faceZ), out);
    }
    return true;
}

function sphereSpherePenetration(sphere: ObjectSphere, cx: number, cy: number, cz: number, radius: number, out: PushOut): boolean {
    const dx = cx - sphere.center.x;
    const dy = cy - sphere.center.y;
    const dz = cz - sphere.center.z;
    const total = sphere.radius + radius;
    const distSq = dx * dx + dy * dy + dz * dz;
    if (distSq >= total * total) return false;
    if (distSq > 1e-12) {
        const dist = Math.sqrt(distSq);
        const push = total - dist;
        out.x = dx / dist * push;
        out.y = dy / dist * push;
        out.z = dz / dist * push;
    } else {
        out.x = total;
        out.y = 0;
        out.z = 0;
    }
    return pushSq(out) > PENETRATION_EPSILON * PENETRATION_EPSILON;
}

function sphereCylinderPenetration(cylinder: ObjectCylinder, cx: number, cy: number, cz: number, radius: number, out: PushOut): boolean {
    const p = toLocalPointAxes(cylinder, cx, cy, cz);
    const radialLen = Math.sqrt(p.x * p.x + p.z * p.z);
    const insideRadial = radialLen <= cylinder.radius;
    const insideHeight = Math.abs(p.y) <= cylinder.halfHeight;

    if (insideRadial && insideHeight) {
        const sideExit = cylinder.radius - radialLen;
        const capExit = cylinder.halfHeight - Math.abs(p.y);
        if (sideExit <= capExit) {
            const nx = radialLen > 1e-8 ? p.x / radialLen : 1;
            const nz = radialLen > 1e-8 ? p.z / radialLen : 0;
            localVectorToWorldAxes(cylinder, nx * (radius + sideExit), 0, nz * (radius + sideExit), out);
        } else {
            localVectorToWorldAxes(cylinder, 0, (p.y >= 0 ? 1 : -1) * (radius + capExit), 0, out);
        }
        return true;
    }

    const radialScale = radialLen > 1e-8 ? Math.min(cylinder.radius, radialLen) / radialLen : 0;
    const qx = p.x * radialScale;
    const qy = math.clamp(p.y, -cylinder.halfHeight, cylinder.halfHeight);
    const qz = p.z * radialScale;
    const dx = p.x - qx;
    const dy = p.y - qy;
    const dz = p.z - qz;
    const distSq = dx * dx + dy * dy + dz * dz;
    if (distSq >= radius * radius) return false;
    const dist = Math.sqrt(Math.max(distSq, 1e-12));
    const push = radius - dist;
    localVectorToWorldAxes(cylinder, dx / dist * push, dy / dist * push, dz / dist * push, out);
    return pushSq(out) > PENETRATION_EPSILON * PENETRATION_EPSILON;
}

function sphereCapsulePenetration(capsule: ObjectCapsule, cx: number, cy: number, cz: number, radius: number, out: PushOut): boolean {
    const p = toLocalPointAxes(capsule, cx, cy, cz);
    const qy = math.clamp(p.y, -capsule.halfSegment, capsule.halfSegment);
    const dx = p.x;
    const dy = p.y - qy;
    const dz = p.z;
    const total = capsule.radius + radius;
    const distSq = dx * dx + dy * dy + dz * dz;
    if (distSq >= total * total) return false;
    if (distSq > 1e-12) {
        const dist = Math.sqrt(distSq);
        const push = total - dist;
        localVectorToWorldAxes(capsule, dx / dist * push, dy / dist * push, dz / dist * push, out);
    } else {
        localVectorToWorldAxes(capsule, total, 0, 0, out);
    }
    return pushSq(out) > PENETRATION_EPSILON * PENETRATION_EPSILON;
}

function capsuleObjectPenetration(
    collider: ObjectCollider,
    cx: number,
    cy: number,
    cz: number,
    halfHeight: number,
    radius: number,
    out: PushOut
): boolean {
    if (collider.kind === 'box') return capsuleBoxPenetration(collider, cx, cy, cz, halfHeight, radius, out);
    let found = false;
    let bestSq = 0;
    for (let i = 0; i < CAPSULE_SAMPLE_COUNT; i++) {
        const t = i / (CAPSULE_SAMPLE_COUNT - 1);
        const y = cy - halfHeight + halfHeight * 2 * t;
        if (!sphereObjectPenetration(collider, cx, y, cz, radius, _scratchPush)) continue;
        const sq = pushSq(_scratchPush);
        if (!found || sq > bestSq) {
            bestSq = sq;
            copyPush(_scratchPush, out);
            found = true;
        }
    }
    return found;
}

function capsuleBoxPenetration(
    box: ObjectBox,
    cx: number,
    cy: number,
    cz: number,
    halfHeight: number,
    radius: number,
    out: PushOut
): boolean {
    let found = false;
    let bestSq = 0;
    for (let i = 0; i < CAPSULE_SAMPLE_COUNT; i++) {
        const t = i / (CAPSULE_SAMPLE_COUNT - 1);
        const y = cy - halfHeight + halfHeight * 2 * t;
        if (!sphereBoxPenetration(box, cx, y, cz, radius, _scratchPush)) continue;
        const sq = pushSq(_scratchPush);
        if (!found || sq > bestSq) {
            bestSq = sq;
            copyPush(_scratchPush, out);
            found = true;
        }
    }
    return found;
}

function rayObjectCollider(
    collider: ObjectCollider,
    ox: number,
    oy: number,
    oz: number,
    dx: number,
    dy: number,
    dz: number,
    maxDist: number
): number | null {
    if (collider.kind === 'box') return rayBox(collider, ox, oy, oz, dx, dy, dz, maxDist);
    if (collider.kind === 'sphere') return raySphere(collider, ox, oy, oz, dx, dy, dz, maxDist);
    return rayBox(objectColliderBoundsBox(collider), ox, oy, oz, dx, dy, dz, maxDist);
}

function objectColliderBoundsBox(collider: ObjectCylinder | ObjectCapsule): ObjectBox {
    const halfY = collider.kind === 'capsule' ? collider.halfHeight : collider.halfHeight;
    return {
        kind: 'box',
        object: collider.object,
        center: collider.center,
        axisX: collider.axisX,
        axisY: collider.axisY,
        axisZ: collider.axisZ,
        halfX: collider.radius,
        halfY,
        halfZ: collider.radius
    };
}

function rayBox(
    box: ObjectBox,
    ox: number,
    oy: number,
    oz: number,
    dx: number,
    dy: number,
    dz: number,
    maxDist: number
): number | null {
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (len < 1e-10) return null;
    dx /= len;
    dy /= len;
    dz /= len;

    const origin = toLocalPoint(box, ox, oy, oz);
    const dir = {
        x: box.axisX.x * dx + box.axisX.y * dy + box.axisX.z * dz,
        y: box.axisY.x * dx + box.axisY.y * dy + box.axisY.z * dz,
        z: box.axisZ.x * dx + box.axisZ.y * dy + box.axisZ.z * dz
    };
    const t = rayAabbLocal(origin, dir, box.halfX, box.halfY, box.halfZ, maxDist);
    return t === null ? null : t;
}

function rayAabbLocal(origin: LocalPoint, dir: LocalPoint, hx: number, hy: number, hz: number, maxDist: number): number | null {
    let tMin = 0;
    let tMax = maxDist;
    const slabs: Array<[number, number, number]> = [
        [origin.x, dir.x, hx],
        [origin.y, dir.y, hy],
        [origin.z, dir.z, hz]
    ];

    for (const [o, d, half] of slabs) {
        if (Math.abs(d) < 1e-10) {
            if (o < -half || o > half) return null;
            continue;
        }
        let t1 = (-half - o) / d;
        let t2 = (half - o) / d;
        if (t1 > t2) [t1, t2] = [t2, t1];
        tMin = Math.max(tMin, t1);
        tMax = Math.min(tMax, t2);
        if (tMin > tMax) return null;
    }

    return tMin <= maxDist ? tMin : null;
}

function raySphere(
    sphere: ObjectSphere,
    ox: number,
    oy: number,
    oz: number,
    dx: number,
    dy: number,
    dz: number,
    maxDist: number
): number | null {
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    if (len < 1e-10) return null;
    dx /= len;
    dy /= len;
    dz /= len;
    const sx = ox - sphere.center.x;
    const sy = oy - sphere.center.y;
    const sz = oz - sphere.center.z;
    const b = sx * dx + sy * dy + sz * dz;
    const c = sx * sx + sy * sy + sz * sz - sphere.radius * sphere.radius;
    const disc = b * b - c;
    if (disc < 0) return null;
    const root = Math.sqrt(disc);
    const nearT = -b - root;
    const farT = -b + root;
    const t = nearT >= 0 ? nearT : farT;
    if (t < 0 || t > maxDist) return null;
    return t;
}

function objectSurfaceNormal(collider: ObjectCollider, x: number, y: number, z: number, rdx: number, rdy: number, rdz: number): Vec3 | null {
    switch (collider.kind) {
        case 'box':
            return boxSurfaceNormal(collider, x, y, z, rdx, rdy, rdz);
        case 'sphere':
            return sphereSurfaceNormal(collider, x, y, z, rdx, rdy, rdz);
        case 'cylinder':
            return cylinderSurfaceNormal(collider, x, y, z, rdx, rdy, rdz);
        case 'capsule':
            return capsuleSurfaceNormal(collider, x, y, z, rdx, rdy, rdz);
    }
}

function boxSurfaceNormal(box: ObjectBox, x: number, y: number, z: number, rdx: number, rdy: number, rdz: number): Vec3 | null {
    const p = toLocalPoint(box, x, y, z);
    if (
        Math.abs(p.x) > box.halfX + SURFACE_NORMAL_EPSILON ||
        Math.abs(p.y) > box.halfY + SURFACE_NORMAL_EPSILON ||
        Math.abs(p.z) > box.halfZ + SURFACE_NORMAL_EPSILON
    ) return null;

    const dx = Math.abs(Math.abs(p.x) - box.halfX);
    const dy = Math.abs(Math.abs(p.y) - box.halfY);
    const dz = Math.abs(Math.abs(p.z) - box.halfZ);
    const normal = new Vec3();
    if (dx <= dy && dx <= dz) normal.copy(box.axisX).mulScalar(p.x >= 0 ? 1 : -1);
    else if (dy <= dz) normal.copy(box.axisY).mulScalar(p.y >= 0 ? 1 : -1);
    else normal.copy(box.axisZ).mulScalar(p.z >= 0 ? 1 : -1);

    if (normal.x * rdx + normal.y * rdy + normal.z * rdz > 0) normal.mulScalar(-1);
    return normal;
}

function sphereSurfaceNormal(sphere: ObjectSphere, x: number, y: number, z: number, rdx: number, rdy: number, rdz: number): Vec3 | null {
    const normal = new Vec3(x - sphere.center.x, y - sphere.center.y, z - sphere.center.z);
    if (normal.lengthSq() > (sphere.radius + SURFACE_NORMAL_EPSILON) * (sphere.radius + SURFACE_NORMAL_EPSILON)) return null;
    if (normal.lengthSq() < 1e-12) normal.set(0, 1, 0);
    else normal.normalize();
    if (normal.x * rdx + normal.y * rdy + normal.z * rdz > 0) normal.mulScalar(-1);
    return normal;
}

function cylinderSurfaceNormal(cylinder: ObjectCylinder, x: number, y: number, z: number, rdx: number, rdy: number, rdz: number): Vec3 | null {
    const p = toLocalPointAxes(cylinder, x, y, z);
    if (Math.abs(p.y) > cylinder.halfHeight + SURFACE_NORMAL_EPSILON) return null;
    const radial = Math.sqrt(p.x * p.x + p.z * p.z);
    if (radial > cylinder.radius + SURFACE_NORMAL_EPSILON) return null;
    const side = Math.abs(radial - cylinder.radius);
    const cap = Math.abs(Math.abs(p.y) - cylinder.halfHeight);
    const normal = new Vec3();
    if (cap <= side) {
        normal.copy(cylinder.axisY).mulScalar(p.y >= 0 ? 1 : -1);
    } else {
        const nx = radial > 1e-8 ? p.x / radial : 1;
        const nz = radial > 1e-8 ? p.z / radial : 0;
        normal.set(
            cylinder.axisX.x * nx + cylinder.axisZ.x * nz,
            cylinder.axisX.y * nx + cylinder.axisZ.y * nz,
            cylinder.axisX.z * nx + cylinder.axisZ.z * nz
        ).normalize();
    }
    if (normal.x * rdx + normal.y * rdy + normal.z * rdz > 0) normal.mulScalar(-1);
    return normal;
}

function capsuleSurfaceNormal(capsule: ObjectCapsule, x: number, y: number, z: number, rdx: number, rdy: number, rdz: number): Vec3 | null {
    const p = toLocalPointAxes(capsule, x, y, z);
    const qy = math.clamp(p.y, -capsule.halfSegment, capsule.halfSegment);
    const dx = p.x;
    const dy = p.y - qy;
    const dz = p.z;
    const distSq = dx * dx + dy * dy + dz * dz;
    if (distSq > (capsule.radius + SURFACE_NORMAL_EPSILON) * (capsule.radius + SURFACE_NORMAL_EPSILON)) return null;
    const dist = Math.sqrt(Math.max(distSq, 1e-12));
    const normal = new Vec3(
        capsule.axisX.x * (dx / dist) + capsule.axisY.x * (dy / dist) + capsule.axisZ.x * (dz / dist),
        capsule.axisX.y * (dx / dist) + capsule.axisY.y * (dy / dist) + capsule.axisZ.y * (dz / dist),
        capsule.axisX.z * (dx / dist) + capsule.axisY.z * (dy / dist) + capsule.axisZ.z * (dz / dist)
    );
    if (normal.lengthSq() < 1e-12) normal.copy(capsule.axisX);
    else normal.normalize();
    if (normal.x * rdx + normal.y * rdy + normal.z * rdz > 0) normal.mulScalar(-1);
    return normal;
}

function isInsideObjectCollider(collider: ObjectCollider, x: number, y: number, z: number): boolean {
    if (collider.kind === 'box') {
        const p = toLocalPoint(collider, x, y, z);
        return Math.abs(p.x) <= collider.halfX && Math.abs(p.y) <= collider.halfY && Math.abs(p.z) <= collider.halfZ;
    }
    if (collider.kind === 'sphere') {
        const dx = x - collider.center.x;
        const dy = y - collider.center.y;
        const dz = z - collider.center.z;
        return dx * dx + dy * dy + dz * dz <= collider.radius * collider.radius;
    }
    if (collider.kind === 'cylinder') {
        const p = toLocalPointAxes(collider, x, y, z);
        return p.x * p.x + p.z * p.z <= collider.radius * collider.radius && Math.abs(p.y) <= collider.halfHeight;
    }
    const p = toLocalPointAxes(collider, x, y, z);
    const qy = math.clamp(p.y, -collider.halfSegment, collider.halfSegment);
    const dx = p.x;
    const dy = p.y - qy;
    const dz = p.z;
    return dx * dx + dy * dy + dz * dz <= collider.radius * collider.radius;
}

function normalizeComponent(dx: number, dy: number, dz: number, component: number): number {
    const len = Math.sqrt(dx * dx + dy * dy + dz * dz);
    return len > 1e-10 ? component / len : 0;
}

function distanceSq(ox: number, oy: number, oz: number, hit: RayHit): number {
    const dx = hit.x - ox;
    const dy = hit.y - oy;
    const dz = hit.z - oz;
    return dx * dx + dy * dy + dz * dz;
}

function pushSq(push: PushOut): number {
    return push.x * push.x + push.y * push.y + push.z * push.z;
}

function copyPush(src: PushOut, dst: PushOut): void {
    dst.x = src.x;
    dst.y = src.y;
    dst.z = src.z;
}

export { SceneObjectCollision, createSceneObjectCollision };
